import { type BarSeriesOption } from 'echarts';
import { util } from 'echarts/core';
import { type GridOption } from 'echarts/types/dist/shared';
import { type ThemeParser } from '../../../core/theme';
import { overrideIfUndefined } from '../../../util/data';
import { type Option } from '../../../core/option';
import { isObject } from '../../../util/utils';
import { getBarColor } from '../static/color';
import { GridThemeParse } from '../component/grid';
import { xAxisPointerThemeParse } from '../component/xAxisPointer';
import { FONT_SIZE_LIST, FONT_WEIGHT_LIST, THS_FONTFAMILY_MEDIUM } from '../static/font';
import { yAxisThemeParse } from '../component/yAxis';
import { xAxisThemeParse } from '../component/xAxis';
import { ToolTipThemeParse } from '../component/toolTip';
import { legendThemeParse } from '../component/legend';
import { judgeIsDynamicBar } from '../../pc/series/dynamicBar';

/**
 * 计算grid的宽高
 * @param dom
 * @param gridOption_
 * @returns
 */
function getGridBoundingRect(dom: HTMLElement, gridOption_: GridOption | Array<GridOption>) {
  const HUNDRED = 100;
  const gridOption = Array.isArray(gridOption_) ? gridOption_ : [gridOption_];
  // echarts的默认值
  const defaultGridOption = {
    top: 60,
    left: '10%',
    right: '10%',
    bottom: 70
  };
  const gridOptionResult = Object.assign(defaultGridOption, gridOption[0]);
  const domWidth = dom.clientWidth;
  const domHeight = dom.clientHeight;
  const topPadding =
    typeof gridOptionResult.top === 'number'
      ? gridOptionResult.top
      : (domHeight * parseFloat(gridOptionResult.top)) / HUNDRED;
  const leftPadding =
    typeof gridOptionResult.left === 'number'
      ? gridOptionResult.left
      : (domWidth * parseFloat(gridOptionResult.left)) / HUNDRED;
  const rightPadding =
    typeof gridOptionResult.right === 'number'
      ? gridOptionResult.right
      : (domWidth * parseFloat(gridOptionResult.right)) / HUNDRED;
  const bottomPadding =
    typeof gridOptionResult.bottom === 'number'
      ? gridOptionResult.bottom
      : (domHeight * parseFloat(gridOptionResult.bottom)) / HUNDRED;

  return {
    x: leftPadding,
    y: topPadding,
    width: domWidth - leftPadding - rightPadding,
    height: domHeight - topPadding - bottomPadding
  };
}

/**
 * 柱间距
 */
export function parseBarGap(seriesOption: BarSeriesOption[], ...args: Parameters<ThemeParser>) {
  const [, , optionToken] = args;
  const groupSize = { single: 0, stack: new Set() };

  seriesOption.forEach(option => {
    // * 不同系列柱子间距和柱宽比例为 2:1
    // eslint-disable-next-line no-param-reassign
    option.barGap = overrideIfUndefined(option.barGap, optionToken.bar.barGap);

    if (typeof option.stack !== 'undefined') {
      groupSize.stack.add(option.stack);
    } else {
      groupSize.single += 1;
    }
  });

  const groupSizeTotal = groupSize.single + groupSize.stack.size;

  if (groupSizeTotal <= 1) {
    seriesOption.forEach(option => {
      // * 单柱情况下，相邻两组间距和柱宽比例为 2:1
      // eslint-disable-next-line no-param-reassign
      option.barCategoryGap = overrideIfUndefined(
        option.barCategoryGap,
        optionToken.bar.barCategoryGap
      );
    });
  } else {
    const barCategoryGap = 2 / ((groupSizeTotal + 1) * 1.5);
    seriesOption.forEach(option => {
      // * 多柱情况下，相邻两组柱间距空1柱宽
      // eslint-disable-next-line no-param-reassign
      option.barCategoryGap = overrideIfUndefined(
        option.barCategoryGap,
        `${barCategoryGap * 100}%`
      );
    });
  }
}

/**
 * 柱的尺寸
 */
export function parseBarSize(seriesOption: BarSeriesOption[], ...args: Parameters<ThemeParser>) {
  const [, , optionToken] = args;

  seriesOption.forEach(option => {
    // * 柱宽
    // eslint-disable-next-line no-param-reassign
    option.barMaxWidth = overrideIfUndefined(option.barMaxWidth, optionToken.bar.barMaxWidth);

    // * 柱高
    // 数据为 0 时的缺失状态
    if (typeof option.stack !== 'undefined') {
      // ! 堆叠的柱子限制柱子的最小高度 2px
      // eslint-disable-next-line no-param-reassign
      option.barMinHeight = overrideIfUndefined(
        option.barMinHeight,
        optionToken.bar.barMinHeight.stack
      );
    } else {
      // eslint-disable-next-line no-param-reassign
      option.barMinHeight = overrideIfUndefined(
        option.barMinHeight,
        optionToken.bar.barMinHeight.single
      );
    }
  });
}

/**
 * 柱的标签
 */
export function parseBarLabel(seriesOption: BarSeriesOption[], ...args: Parameters<ThemeParser>) {
  const [, , optionToken] = args;

  seriesOption.forEach(option => {
    
    // ! 堆叠的柱子不处理标签
    if (typeof option.stack !== 'undefined') {
      return;
    }

    if (typeof option.label === 'undefined') {
      // eslint-disable-next-line no-param-reassign
      option.label = {};
    }

    if (typeof option.label.position === 'undefined') {
      // * 标签放置在正负值方向的位置上
      // @ts-ignore
      // eslint-disable-next-line no-param-reassign
      option.label.dvPosition = 'valueDirection';
    }

    // * 标签文本颜色继承自系列色
    // eslint-disable-next-line no-param-reassign
    option.label.color = overrideIfUndefined(option.label.color, 'inherit');

    // eslint-disable-next-line no-param-reassign
    option.label.fontWeight = overrideIfUndefined(
      option.label.fontWeight,
      FONT_WEIGHT_LIST.barLabel
    );

    // eslint-disable-next-line no-param-reassign
    option.label.fontSize = overrideIfUndefined(
      option.label.fontSize,
      FONT_SIZE_LIST.seriesLabel
    );

    // eslint-disable-next-line no-param-reassign
    option.label.fontFamily = overrideIfUndefined(
      option.label.fontFamily,
      THS_FONTFAMILY_MEDIUM
    );

    // * 开启标签展示
    // eslint-disable-next-line no-param-reassign
    option.label.show = overrideIfUndefined(option.label.show, true);
  });
}

export function parseBarColor(seriesOption: BarSeriesOption[], ...args: Parameters<ThemeParser>) {
  const [, , optionToken] = args;
  // echarts默认会在高亮状态给柱子颜色增加明亮度，范式要求去除这种效果
  const defaultEmphasis = {
    disabled: true
  };

  seriesOption.forEach((option, index) => {
    option.emphasis = util.merge(option.emphasis || {}, defaultEmphasis);
    const themeBarColor = getBarColor(index, seriesOption.length === 1, optionToken);
    if (!isObject(option.itemStyle)) {
      const itemStyle = {
        color: themeBarColor
      };
      option.itemStyle = itemStyle;
    } else {
      option.itemStyle.color = overrideIfUndefined(option.itemStyle.color, themeBarColor);
    }
  });
}
/**
 * 计算柱的圆角
 * 柱宽>=8时，圆角2
 * 柱宽<8，>=4，圆角1
 * 柱宽<4，圆角0
 * @param barWidth 柱子宽度
 */
function calcBorderRadius(barWidth: number) {
  const BAR_WIDTH_8 = 8;
  const BAR_WIDTH_4 = 4;
  const BORDER_RADIUS_2 = 2;
  const BORDER_RADIUS_1 = 1;
  const BORDER_RADIUS_0 = 0;
  let borderRadius: number;
  if (barWidth < BAR_WIDTH_8 && barWidth >= BAR_WIDTH_4) {
    borderRadius = BORDER_RADIUS_1;
  } else if (barWidth < BAR_WIDTH_4) {
    borderRadius = BORDER_RADIUS_0;
  } else {
    borderRadius = BORDER_RADIUS_2;
  }
  return borderRadius;
}

function calcBarWidth(seriesOption: BarSeriesOption[], dom: HTMLElement, option: Option) {
  const UNITWIDTHTIMES = 2;
  const gridRect = getGridBoundingRect(dom, option.grid);
  const barNum = seriesOption[0].data.length;
  return Math.min(gridRect.width / barNum / UNITWIDTHTIMES, seriesOption[0].barMaxWidth);
}

/**
 * 柱的动画
 */
function parseBarAnimation(seriesOption: BarSeriesOption[], ...args: Parameters<ThemeParser>) {
  const [, chart, optionToken] = args;

  seriesOption.forEach(option => {
    // 要根据画布高度确定动画效果
    const { height } = chart.getDom().getBoundingClientRect();
    const animationOpts = optionToken.bar.animation.find(
      item => item.condition.minH < height && height <= item.condition.maxH
    );

    if (animationOpts) {
      option.animationDuration = overrideIfUndefined(option.animationDuration, animationOpts.time);
      // @ts-ignore
      option.animationEasing = overrideIfUndefined(option.animationEasing, animationOpts.easing);
      option.animationDurationUpdate = overrideIfUndefined(
        option.animationDurationUpdate,
        animationOpts.time
      );
      option.animationEasingUpdate = overrideIfUndefined(
        option.animationEasingUpdate,
        // @ts-ignore
        animationOpts.easing
      );
    }

    // * 开启动画
    option.animation = overrideIfUndefined(option.animation, true);
    /* eslint-enable no-param-reassign */
  });
}

function ensureItemStyle(option: BarSeriesOption) {
  if (typeof option.itemStyle === 'undefined') {
    option.itemStyle = {};
  }
}

function ensureData(option: BarSeriesOption) {
  if (typeof option.data === 'undefined') {
    option.data = [];
  }
}

function parseBarBorderRadius(seriesOption: BarSeriesOption[], dom: HTMLElement, option: Option) {
  const barWidth = calcBarWidth(seriesOption, dom, option);
  seriesOption.forEach(el => {
    ensureItemStyle(el);
    if (typeof el.itemStyle.borderRadius === 'undefined') {
      ensureData(el);
      el.data = el.data.map(item => {
        if (util.isString(item) || !util.isObject(item)) {
          // eslint-disable-next-line no-param-reassign
          item = {
            value: item
          };
        }
        // @ts-ignore
        if (typeof item.itemStyle === 'undefined') {
          // @ts-ignore
          // eslint-disable-next-line no-param-reassign
          item.itemStyle = {};
        }
        ensureItemStyle(item);
        const borderRadius = calcBorderRadius(barWidth);
        // @ts-ignore
        // eslint-disable-next-line no-param-reassign
        item.itemStyle.borderRadius = overrideIfUndefined(
          // @ts-ignore
          item.itemStyle.borderRadius,
          // @ts-ignore
          Number(item.value) >= 0
            ? [borderRadius, borderRadius, 0, 0]
            : [0, 0, borderRadius, borderRadius]
        );
        return item;
      });
    }
  });

  // 消除不在最上面的柱子的圆角
  seriesOption
    .filter(bar => bar.stack)
    .forEach((el, index, arr) => {
      el.data.forEach((item, j) => {
        if (item.value !== null && item.value !== undefined) {
          for (let i = 0; i < index; i++) {
            arr[i].data[j].itemStyle.borderRadius = undefined;
          }
        }
      });
    });
}

export function judgeIsNormalBar(option: Option) {
  if (typeof option?.series === 'undefined') {
    return false;
  }
  const barSeries = option.series.filter(item => item.type === 'bar');
  if (barSeries.length === 0) {
    return false;
  }

  // 若是动态条形图则不进行以下柱状图主题解析
  if (judgeIsDynamicBar(barSeries)) {
    return false;
  }
  return true;
}

/**
 * 移动端主题-柱状图解析器
 */
export function barThemeParse(...args: Parameters<ThemeParser>) {
  const [option, chart] = args;
  if (!judgeIsNormalBar(option)) {
    return;
  }
  const barSeries = option.series.filter(item => item.type === 'bar');
  parseBarSize(barSeries, ...args);
  parseBarGap(barSeries, ...args);
  parseBarLabel(barSeries, ...args);
  parseBarAnimation(barSeries, ...args);
  parseBarColor(barSeries, ...args);
  parseBarBorderRadius(barSeries, chart.getDom(), option);
  // component按图表需要引入
  GridThemeParse(...args);
  xAxisPointerThemeParse(...args);
  ToolTipThemeParse(...args);

  xAxisThemeParse(...args);
  yAxisThemeParse(...args);
  legendThemeParse(...args);
}
